/**
 * NeoCoreNotificacionesPage — Centro de Notificaciones.
 *
 * Control central de TODO lo que sale de Neocoop:
 *   · interno → WhatsApp al equipo (notificar-evento-interno)
 *   · push    → al asociado (encolar_push → push_outbox → Expo)
 *
 * Permite: ver el estado de cada canal, prender/apagar cada evento, y un feed
 * unificado de lo último que salió. Fuente: RPCs notif_centro_*.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { ArrowsClockwise, BellRinging, DeviceMobile, ChatCircleText, CheckCircle, XCircle, Clock, PaperPlaneTilt, MagnifyingGlass, WhatsappLogo } from '@phosphor-icons/react'
import { supabase } from '@/lib/supabase'

type Resumen = {
  push: { habilitado: boolean; tokens_activos: number; enviados_hoy: number; pendientes: number; fallidos_hoy: number; eventos_on: number; eventos_total: number }
  interno: { enviadas_hoy: number; omitidas_hoy: number; eventos_on: number; eventos_total: number }
}
type Cfg = { canal: 'interno' | 'push'; evento: string; etiqueta: string; descripcion: string | null; activo: boolean }
type Feed = { canal: 'interno' | 'push'; evento: string; titulo: string | null; cuerpo: string | null; estado: string | null; error: string | null; at: string; quien: string | null; id: string }

const OK = 'var(--ok, #10b981)'
const BRAND = 'var(--brand-500, #FF5B04)'
const DANGER = 'var(--danger, #e5484d)'
const fmtHora = (iso: string) => new Date(iso).toLocaleString('es-GT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false })

export function NeoCoreNotificacionesPage() {
  const [res, setRes] = useState<Resumen | null>(null)
  const [cfg, setCfg] = useState<Cfg[]>([])
  const [feed, setFeed] = useState<Feed[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [canalFeed, setCanalFeed] = useState<'todos' | 'interno' | 'push'>('todos')
  const [busy, setBusy] = useState<string | null>(null)

  const load = useCallback(async () => {
    setError(null)
    const [r, c, f] = await Promise.all([
      supabase.rpc('notif_centro_resumen'),
      supabase.rpc('notif_centro_config'),
      supabase.rpc('notif_centro_feed', { p_canal: null, p_limit: 80 }),
    ])
    if (r.error || c.error || f.error) { setError((r.error || c.error || f.error)?.message ?? 'Error'); setLoading(false); return }
    setRes(r.data as Resumen); setCfg((c.data as Cfg[]) ?? []); setFeed((f.data as Feed[]) ?? [])
    setLoading(false)
  }, [])
  useEffect(() => { void load() }, [load])

  const toggle = async (row: Cfg) => {
    setBusy(`${row.canal}:${row.evento}`)
    setCfg((prev) => prev.map((x) => (x.canal === row.canal && x.evento === row.evento ? { ...x, activo: !x.activo } : x)))
    const { error } = await supabase.rpc('notif_centro_toggle', { p_canal: row.canal, p_evento: row.evento, p_activo: !row.activo })
    setBusy(null)
    if (error) { setCfg((prev) => prev.map((x) => (x.canal === row.canal && x.evento === row.evento ? { ...x, activo: row.activo } : x))); setError(error.message) }
  }

  const togglePushMaster = async () => {
    if (!res) return
    const next = !res.push.habilitado
    setBusy('push-master')
    setRes((p) => (p ? { ...p, push: { ...p.push, habilitado: next } } : p))
    const { error } = await supabase.rpc('notif_centro_push_master', { p_activo: next })
    setBusy(null)
    if (error) { setRes((p) => (p ? { ...p, push: { ...p.push, habilitado: !next } } : p)); setError(error.message) }
  }

  const interno = useMemo(() => cfg.filter((c) => c.canal === 'interno'), [cfg])
  const push = useMemo(() => cfg.filter((c) => c.canal === 'push'), [cfg])
  const feedFiltrado = useMemo(() => (canalFeed === 'todos' ? feed : feed.filter((f) => f.canal === canalFeed)), [feed, canalFeed])

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--n-500)' }}>Cargando…</div>

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <BellRinging size={20} style={{ color: BRAND }} weight="fill" />
            <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--n-900)' }}>Centro de notificaciones</span>
          </div>
          <div style={{ fontSize: 12, color: 'var(--n-500)', marginTop: 2 }}>Todo lo que le sale al equipo y al asociado, en un solo lugar.</div>
        </div>
        <button className="btn btn-ghost" onClick={() => void load()}><ArrowsClockwise size={16} /> Actualizar</button>
      </div>

      {error && <div className="error-banner" style={{ margin: 0 }}>{error}</div>}

      {/* Estado de canales */}
      {res && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 12 }}>
          {/* PUSH */}
          <div className="card" style={{ padding: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <DeviceMobile size={18} style={{ color: 'var(--n-700)' }} />
                <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--n-900)' }}>Push al asociado</span>
              </div>
              <Switch on={res.push.habilitado} busy={busy === 'push-master'} onClick={togglePushMaster} />
            </div>
            <div style={{ fontSize: 11, color: 'var(--n-500)', marginTop: 4 }}>
              Interruptor maestro. Si está apagado, no sale ningún push (nada se encola).
            </div>
            <div style={{ display: 'flex', gap: 16, marginTop: 12, flexWrap: 'wrap' }}>
              <Mini value={res.push.tokens_activos} label="con app (tokens)" />
              <Mini value={res.push.enviados_hoy} label="enviados hoy" color={OK} />
              <Mini value={res.push.pendientes} label="en cola" color={res.push.pendientes ? BRAND : undefined} />
              <Mini value={res.push.fallidos_hoy} label="fallidos hoy" color={res.push.fallidos_hoy ? DANGER : undefined} />
              <Mini value={`${res.push.eventos_on}/${res.push.eventos_total}`} label="eventos on" />
            </div>
          </div>
          {/* INTERNO */}
          <div className="card" style={{ padding: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ChatCircleText size={18} style={{ color: 'var(--n-700)' }} />
              <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--n-900)' }}>WhatsApp al equipo</span>
            </div>
            <div style={{ fontSize: 11, color: 'var(--n-500)', marginTop: 4 }}>
              Avisos internos (con wa.me para reenviar al asociado de un toque).
            </div>
            <div style={{ display: 'flex', gap: 16, marginTop: 12, flexWrap: 'wrap' }}>
              <Mini value={res.interno.enviadas_hoy} label="enviadas hoy" color={OK} />
              <Mini value={res.interno.omitidas_hoy} label="omitidas (off)" color={res.interno.omitidas_hoy ? BRAND : undefined} />
              <Mini value={`${res.interno.eventos_on}/${res.interno.eventos_total}`} label="eventos on" />
            </div>
          </div>
        </div>
      )}

      {/* Enviar aviso manual a un asociado */}
      <EnviarManualCard onSent={() => void load()} />

      {/* Configuración: toggles por evento */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 12 }}>
        <ConfigGroup titulo="Push al asociado" icon={<DeviceMobile size={16} />} rows={push} onToggle={toggle} busy={busy} />
        <ConfigGroup titulo="WhatsApp al equipo" icon={<ChatCircleText size={16} />} rows={interno} onToggle={toggle} busy={busy} />
      </div>

      {/* Feed unificado */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderBottom: '1px solid var(--n-100)' }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--n-900)' }}>Últimas notificaciones</span>
          <div style={{ display: 'flex', gap: 6 }}>
            {(['todos', 'push', 'interno'] as const).map((c) => (
              <button key={c} onClick={() => setCanalFeed(c)}
                style={{ fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 20, cursor: 'pointer',
                  border: '1px solid ' + (canalFeed === c ? 'var(--n-900)' : 'var(--n-200)'),
                  background: canalFeed === c ? 'var(--n-900)' : 'var(--surface,#fff)',
                  color: canalFeed === c ? '#fff' : 'var(--n-600)' }}>
                {c === 'todos' ? 'Todo' : c === 'push' ? 'Push' : 'Equipo'}
              </button>
            ))}
          </div>
        </div>
        {feedFiltrado.length === 0 ? (
          <div style={{ padding: 28, textAlign: 'center', color: 'var(--n-400)', fontSize: 13 }}>Sin notificaciones todavía.</div>
        ) : (
          <div>
            {feedFiltrado.map((f) => (
              <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderBottom: '1px solid var(--n-100)' }}>
                <EstadoIcon canal={f.canal} estado={f.estado} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: 'var(--n-800)', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {f.titulo ?? f.evento}{f.quien ? <span style={{ fontWeight: 400, color: 'var(--n-500)' }}> · {f.quien}</span> : null}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--n-400)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {f.cuerpo ?? ''}
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 12, color: f.canal === 'push' ? 'var(--n-700)' : BRAND, background: f.canal === 'push' ? 'var(--n-100)' : 'var(--brand-50,#fff5ee)' }}>
                    {f.canal === 'push' ? 'PUSH' : 'EQUIPO'}
                  </span>
                  <div style={{ fontSize: 10, color: 'var(--n-400)', marginTop: 3 }}>{fmtHora(f.at)}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <p style={{ color: 'var(--n-400)', fontSize: 11, margin: 0 }}>
        Apagar un evento lo detiene al instante (no se encola ni se envía). El push depende también del interruptor maestro y de que el asociado tenga la app. Los avisos al equipo quedan registrados; los push guardan su estado de entrega.
      </p>
    </div>
  )
}

type Hit = { id: string; neo: string | null; nombre: string; telefono: string | null; tiene_token: boolean }
type EnvioRes = { asociado: string; tiene_token: boolean; push_habilitado: boolean; encolado: boolean; telefono: string | null; mensaje: string }

function EnviarManualCard({ onSent }: { onSent: () => void }) {
  const [q, setQ] = useState('')
  const [hits, setHits] = useState<Hit[]>([])
  const [sel, setSel] = useState<Hit | null>(null)
  const [titulo, setTitulo] = useState('')
  const [cuerpo, setCuerpo] = useState('')
  const [busy, setBusy] = useState(false)
  const [res, setRes] = useState<EnvioRes | null>(null)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    if (sel || q.trim().length < 2) { setHits([]); return }
    let alive = true
    const t = setTimeout(async () => {
      const { data } = await supabase.rpc('notif_buscar_asociado', { p_q: q.trim() })
      if (alive) setHits((data as Hit[]) ?? [])
    }, 250)
    return () => { alive = false; clearTimeout(t) }
  }, [q, sel])

  const enviar = async () => {
    if (!sel) { setErr('Elegí un asociado.'); return }
    setBusy(true); setErr(null); setRes(null)
    const { data, error } = await supabase.rpc('notif_enviar_manual', {
      p_asociado_id: sel.id, p_titulo: titulo, p_cuerpo: cuerpo, p_deeplink: null,
    })
    setBusy(false)
    if (error) { setErr(error.message); return }
    setRes(data as EnvioRes)
    setTitulo(''); setCuerpo('')
    onSent()
  }

  const waHref = res?.telefono ? `https://wa.me/${res.telefono}?text=${encodeURIComponent(res.mensaje)}` : null

  return (
    <div className="card" style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <PaperPlaneTilt size={18} style={{ color: BRAND }} weight="fill" />
        <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--n-900)' }}>Enviar aviso manual</span>
        <span style={{ fontSize: 11, color: 'var(--n-500)' }}>· push al asociado (+ wa.me de respaldo)</span>
      </div>

      {/* Selector de asociado */}
      {sel ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 9, background: 'var(--n-50,#f7f7f8)', marginBottom: 10 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--n-800)' }}>{sel.nombre}</span>
          {sel.neo && <span style={{ fontSize: 11, color: 'var(--n-500)' }}>Neo.id {sel.neo}</span>}
          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 12, color: sel.tiene_token ? OK : 'var(--n-500)', background: sel.tiene_token ? 'rgba(16,185,129,.12)' : 'var(--n-100)' }}>
            {sel.tiene_token ? 'tiene app' : 'sin app · irá por wa.me'}
          </span>
          <button onClick={() => { setSel(null); setQ(''); setRes(null) }} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--n-500)', cursor: 'pointer', fontSize: 12 }}>cambiar</button>
        </div>
      ) : (
        <div style={{ position: 'relative', marginBottom: 10 }}>
          <MagnifyingGlass size={15} style={{ position: 'absolute', left: 10, top: 10, color: 'var(--n-400)' }} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar asociado por nombre o Neo.id…"
            style={{ padding: '9px 10px 9px 30px', borderRadius: 9, border: '1px solid var(--n-200)', fontSize: 13, width: '100%', background: 'var(--surface,#fff)', color: 'var(--n-800)' }} />
          {hits.length > 0 && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20, marginTop: 4, background: 'var(--surface,#fff)', border: '1px solid var(--n-200)', borderRadius: 9, boxShadow: '0 8px 24px rgba(0,0,0,.1)', maxHeight: 240, overflowY: 'auto' }}>
              {hits.map((h) => (
                <button key={h.id} onClick={() => { setSel(h); setHits([]) }}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', padding: '8px 10px', background: 'none', border: 'none', borderBottom: '1px solid var(--n-100)', cursor: 'pointer' }}>
                  <span style={{ fontSize: 13, color: 'var(--n-800)', flex: 1 }}>{h.nombre}</span>
                  {h.neo && <span style={{ fontSize: 11, color: 'var(--n-400)' }}>{h.neo}</span>}
                  {h.tiene_token && <span style={{ width: 7, height: 7, borderRadius: '50%', background: OK }} title="tiene app" />}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <input value={titulo} onChange={(e) => setTitulo(e.target.value)} placeholder="Título (header)" maxLength={80}
        style={{ padding: '9px 10px', borderRadius: 9, border: '1px solid var(--n-200)', fontSize: 13, width: '100%', background: 'var(--surface,#fff)', color: 'var(--n-800)', marginBottom: 8, fontWeight: 600 }} />
      <textarea value={cuerpo} onChange={(e) => setCuerpo(e.target.value)} placeholder="Mensaje…" rows={3} maxLength={500}
        style={{ padding: '9px 10px', borderRadius: 9, border: '1px solid var(--n-200)', fontSize: 13, width: '100%', background: 'var(--surface,#fff)', color: 'var(--n-800)', resize: 'vertical', fontFamily: 'inherit' }} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
        <button className="btn" disabled={busy || !sel || !titulo.trim() || !cuerpo.trim()} onClick={enviar}>
          {busy ? 'Enviando…' : <><PaperPlaneTilt size={15} /> Enviar</>}
        </button>
        {err && <span style={{ fontSize: 12, color: DANGER }}>{err}</span>}
      </div>

      {res && (
        <div style={{ marginTop: 12, padding: 10, borderRadius: 9, background: 'var(--n-50,#f7f7f8)', fontSize: 12, color: 'var(--n-700)' }}>
          {res.encolado
            ? <span style={{ color: OK, fontWeight: 600 }}>✓ Push enviado a {res.asociado}.</span>
            : <span>El push no salió {res.push_habilitado ? '(el asociado no tiene la app)' : '(el push maestro está apagado)'}.</span>}
          {waHref && (
            <div style={{ marginTop: 6 }}>
              <a href={waHref} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#25D366', fontWeight: 600, textDecoration: 'none' }}>
                <WhatsappLogo size={16} weight="fill" /> Enviarlo también por WhatsApp
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function Switch({ on, busy, onClick }: { on: boolean; busy: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick} disabled={busy} aria-pressed={on}
      style={{ width: 44, height: 26, borderRadius: 20, border: 'none', cursor: busy ? 'wait' : 'pointer', position: 'relative',
        background: on ? OK : 'var(--n-300)', transition: 'background .15s', opacity: busy ? 0.6 : 1, flexShrink: 0 }}>
      <span style={{ position: 'absolute', top: 3, left: on ? 21 : 3, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left .15s', boxShadow: '0 1px 2px rgba(0,0,0,.2)' }} />
    </button>
  )
}

function Mini({ value, label, color }: { value: number | string; label: string; color?: string }) {
  return (
    <div>
      <div style={{ fontSize: 18, fontWeight: 700, color: color ?? 'var(--n-900)', lineHeight: 1.1 }}>{value}</div>
      <div style={{ fontSize: 10, color: 'var(--n-500)' }}>{label}</div>
    </div>
  )
}

function ConfigGroup({ titulo, icon, rows, onToggle, busy }: { titulo: string; icon: React.ReactNode; rows: Cfg[]; onToggle: (r: Cfg) => void; busy: string | null }) {
  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 14px', borderBottom: '1px solid var(--n-100)' }}>
        <span style={{ color: 'var(--n-700)' }}>{icon}</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--n-900)' }}>{titulo}</span>
        <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--n-400)' }}>{rows.filter((r) => r.activo).length}/{rows.length} activos</span>
      </div>
      <div>
        {rows.map((r) => (
          <div key={r.evento} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', borderBottom: '1px solid var(--n-100)' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: r.activo ? 'var(--n-800)' : 'var(--n-400)' }}>{r.etiqueta}</div>
              {r.descripcion && <div style={{ fontSize: 11, color: 'var(--n-400)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.descripcion}</div>}
            </div>
            <Switch on={r.activo} busy={busy === `${r.canal}:${r.evento}`} onClick={() => onToggle(r)} />
          </div>
        ))}
      </div>
    </div>
  )
}

function EstadoIcon({ canal, estado }: { canal: string; estado: string | null }) {
  if (canal === 'push') {
    if (estado === 'sent') return <CheckCircle size={18} weight="fill" style={{ color: OK, flexShrink: 0 }} />
    if (estado === 'error' || estado === 'failed') return <XCircle size={18} weight="fill" style={{ color: DANGER, flexShrink: 0 }} />
    return <Clock size={18} style={{ color: BRAND, flexShrink: 0 }} />
  }
  if (estado === 'omitida') return <XCircle size={18} style={{ color: 'var(--n-300)', flexShrink: 0 }} />
  return <PaperPlaneTilt size={18} weight="fill" style={{ color: BRAND, flexShrink: 0 }} />
}

export default NeoCoreNotificacionesPage
